import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Shield, Menu, X, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";

const navLinks = [
  { label: "Features", href: "/features" },
  { label: "Solutions", href: "/solutions" },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openAuth = (tab) => {
    setAuthTab(tab);
    setAuthOpen(true);
    setMobileOpen(false);
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-cream/90 border-b border-ink/8"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 flex-shrink-0">
              <div className="w-7 h-7 bg-forest rounded-md flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span
                className={`text-base font-semibold ${scrolled ? "text-ink" : "text-white"}`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                NextTackle
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    location.pathname === link.href
                      ? scrolled ? "text-forest" : "text-white"
                      : scrolled
                      ? "text-ink/70 hover:text-ink"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  <span className={`text-sm font-medium ${scrolled ? "text-ink/70" : "text-white/70"}`}>
                    Hi, {user.name}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => navigate("/dashboard")}
                    data-testid="nav-dashboard-btn"
                    className="bg-forest text-cream rounded-full font-medium text-sm hover:bg-forest/90 transition-colors"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-1.5" />
                    Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={logout}
                    data-testid="nav-logout-btn"
                    className={`rounded-full text-sm border-ink/20 ${scrolled ? "text-ink" : "text-white border-white/30 bg-transparent"}`}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => openAuth("login")}
                    data-testid="nav-login-btn"
                    className={`text-sm font-medium px-3 py-2 transition-colors ${
                      scrolled ? "text-ink/70 hover:text-ink" : "text-white/70 hover:text-white"
                    }`}
                  >
                    Login
                  </button>
                  <Button
                    onClick={() => openAuth("register")}
                    data-testid="nav-signup-btn"
                    className="bg-forest text-cream rounded-full font-medium text-sm hover:bg-forest/90 transition-colors"
                  >
                    Start Free Trial
                  </Button>
                </>
              )}
            </div>

            <button
              className={`md:hidden p-2 ${scrolled ? "text-ink" : "text-white"}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-cream border-t border-ink/5 px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                className="block px-3 py-2 text-sm font-medium text-ink/70 hover:text-forest rounded-full"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-ink/5 flex flex-col gap-2 mt-2">
              {user ? (
                <>
                  <Button
                    className="w-full bg-forest text-cream rounded-full hover:bg-forest/90 transition-colors"
                    onClick={() => { navigate("/dashboard"); setMobileOpen(false); }}
                    data-testid="nav-mobile-dashboard-btn"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-1.5" />
                    Dashboard
                  </Button>
                  <Button variant="outline" onClick={logout} className="w-full rounded-full border-ink/20 text-ink" data-testid="nav-mobile-logout-btn">
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => openAuth("login")}
                    data-testid="nav-mobile-login-btn"
                    className="w-full text-left px-3 py-2 text-sm font-medium text-ink/70"
                  >
                    Login
                  </button>
                  <Button
                    onClick={() => openAuth("register")}
                    data-testid="nav-mobile-signup-btn"
                    className="w-full bg-forest text-cream rounded-full hover:bg-forest/90"
                  >
                    Start Free Trial
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab={authTab} />
    </>
  );
}
